import { css } from '@emotion/react';
import { i18next, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useLocation, useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { Heading, NakedButton, PopoverMenu, PopoverMenuItem, Text, usePopoverMenu } from '@frontend/design-system';
import { useCollectPaymentMultiContext } from '../..';

type NavigationHeaderProps = {
  title: string;
};

export const NavigationHeader = ({ title }: NavigationHeaderProps) => {
  const { t } = useTranslation('payments');
  const currentPath = useLocation().pathname;
  const navigate = useNavigate();
  const {
    multiStep: { onCancel },
  } = useCollectPaymentMultiContext();
  const { getMenuProps, getTriggerProps, getItemProps, isOpen } = usePopoverMenu({
    placement: 'bottom-start',
    middlewareOptions: { offset: 4 },
  });

  const handleClick = (path: string) => {
    if (currentPath !== path) {
      navigate({ to: path });
    } else {
      onCancel?.();
    }
  };

  return (
    <div css={{ padding: theme.spacing(2, 3) }}>
      <Text size='large'>{t('Payments')}</Text>
      <NakedButton
        css={css`
          display: flex;
          align-items: center;
          gap: ${theme.spacing(1)};
        `}
        {...getTriggerProps()}
      >
        <Heading level={2}>{title}</Heading>
        <span
          css={[
            css`
              border-radius: ${theme.borderRadius.medium};
              :hover,
              :focus {
                background: ${theme.colors.neutral20};
              }
            `,
          ]}
          style={{
            background: isOpen ? theme.colors.primary20 : undefined,
          }}
        >
          <Icon
            name='caret-down'
            css={css`
              transition: transform 150ms ease-out;
              transform: rotate(${isOpen ? '180deg' : '0deg'});
            `}
          />
        </span>
      </NakedButton>
      <PopoverMenu
        {...getMenuProps()}
        css={css`
          min-width: 220px;
          max-width: 300px;
        `}
      >
        {subNavItems?.map((subNavItem, index) => (
          <PopoverMenuItem
            preload={'intent'}
            preloadIntentProximity={100}
            key={subNavItem.path}
            css={css`
              color: ${theme.font.colors.default};
              justify-content: space-between;
              width: 100%;
            `}
            {...getItemProps({
              index,
              onClick: () => handleClick(subNavItem.path),
            })}
          >
            <span>{subNavItem.label}</span>
          </PopoverMenuItem>
        ))}
      </PopoverMenu>
    </div>
  );
};

const subNavItems = [
  { label: i18next.t('Requests', { ns: 'payments' }), path: '/payments/invoices' },
  { label: i18next.t('Payouts', { ns: 'payments' }), path: '/payments/payouts' },
  { label: i18next.t('Payment Plans', { ns: 'payments' }), path: '/payments/payment-plans' },
];
